<script setup>
import { computed, inject, ref } from 'vue';
import { visibleStats } from '../statSources.js';
import { countUpText } from '../countUp.js';
import { useCountUp } from '../useCountUp.js';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// Slot resolution is Classic's, deliberately unchanged: a slot is
// either a free-form { value, label } pair or a reference to an
// auto-computed metric, and every slot can override the label and
// suffix the number with "+". Two templates reading the same config
// have to answer the same numbers.
// A zero or missing number is left out for a visitor, and a bar with
// nothing from the data is not drawn (visibleStats says why).
const editing = inject('tiqEditorCanvas', false);
const stats = computed(() => visibleStats(props.config, props.site_data?.stats || {}, editing));

// "Count up": the numbers run from zero when the bar scrolls into view.
const root = ref(null);
const progress = useCountUp(root, () => props.config.count_up);
const shown = (value) => countUpText(value, progress.value);

// A slot is free text as well as a number, and a word like YEAR-ROUND
// at the number's size is wider than its quarter of the row: it broke
// mid-word into YEAR- / ROUN / D. The value is sized to fit its
// longest word instead (see .th-stats__value), so a number keeps the
// full size and a word comes down only as far as it has to.
const longestWord = (value) => Math.max(1, ...String(value ?? '').split(/\s+/).map((w) => w.length));
</script>

<template>
    <section v-if="stats.length" ref="root" :id="config.anchor_id || null" class="th-block th-stats" :style="styleVars(config)">
        <div class="th-wrap">
            <div class="th-stats__row">
                <div v-for="(stat, i) in stats" :key="i" class="th-stats__cell">
                    <div class="th-stats__value" :style="{ '--th-stat-chars': longestWord(stat.value) }">{{ shown(stat.value) }}</div>
                    <div v-if="stat.label" class="th-stats__label">{{ stat.label }}</div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-stats { padding-top: clamp(40px, 6vw, 72px); }
/* Half the gap below, like every other section, so the block after it
   lands the design's gap away rather than flush. */
.tpl-trailhead .th-stats { padding-bottom: clamp(14px, 2vw, 24px); }
/* Four to a row, or two once the row is narrower than 640px, never
   three-and-one. The clamp picks a quarter-width minimum on a wide
   row and a half-width one on a narrow row: (640px - 100%) * 999 is
   hugely negative when the row is wider than 640px and hugely
   positive when it is narrower. */
.th-stats__row {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(clamp(calc(25% - 18px), (640px - 100%) * 999, calc(50% - 12px)), 1fr));
    column-gap: 24px;
}
/* Each number carries its own rule, so the gaps between them break
   the line the way the design draws it. */
.th-stats__cell {
    min-width: 0;
    container-type: inline-size;
    padding: clamp(12px, 2vw, 20px) 0 clamp(12px, 2vw, 4px);
    border-top: 2px solid var(--th-line);
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.th-stats__value {
    font-variant-numeric: tabular-nums;
    font: 800 clamp(34px, 5vw, 64px)/1 var(--th-display);
    /* ~0.72em per capital in the wide display face, so the longest
       word fits the cell; a short number is unaffected by the min(). */
    font-size: min(clamp(34px, 5vw, 64px), calc(100cqi / (var(--th-stat-chars, 1) * 0.72)));
    font-stretch: 110%;
    color: var(--text-heading, var(--accent));
    overflow-wrap: anywhere;
}
.th-stats__label {
    font: 500 clamp(10px, 1.1vw, 12px)/1.3 var(--th-mono);
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--text-sub, var(--th-muted));
}
</style>
